import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useVisibilityAwareInterval } from '../../hooks/useVisibilityAwareInterval';
import {
  ArrowPathIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  InformationCircleIcon,
  ShieldExclamationIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import type { ColumnDef } from '@tanstack/react-table';
import { TanTable } from '../../components/TanTable';
import { Drawer } from '../../components/ui/Drawer';
import adminService, {
  SecurityAuditLog,
  SecurityAuditLogsStatsResponse,
} from '../../services/admin.service';
import { LOG_FETCH_ALL_LIMIT, LOG_AUTO_REFRESH_MS, DetailField, NONE } from './logPageShared';

/**
 * Logging → Security Audit Log.
 *
 * Pull-all + TanTable virtualization, detail in a slide-out drawer — same
 * pattern as System Logs. Row cells stay single-line because the virtualizer
 * assumes a fixed row height; the details JSON, user agent and full endpoint
 * live in the drawer.
 */

const SEVERITY_CLASSES: Record<string, string> = {
  low: 'bg-gray-100 text-gray-800 border-rule dark:bg-gray-700 dark:text-gray-200',
  medium: 'bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-300 dark:border-yellow-800',
  high: 'bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-800',
  critical: 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800',
};

const SeverityBadge: React.FC<{ severity: string }> = ({ severity }) => (
  <span
    className={`px-2 py-0.5 rounded text-xs font-medium border inline-flex items-center gap-1 ${
      SEVERITY_CLASSES[severity] || SEVERITY_CLASSES.medium
    }`}
  >
    {(severity === 'high' || severity === 'critical') && (
      <ExclamationTriangleIcon className="w-3 h-3" />
    )}
    {severity.toUpperCase()}
  </span>
);

const getEventTypeBadgeColor = (eventType: string) => {
  if (eventType.includes('violation')) {
    return 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800';
  }
  if (eventType.includes('failed') || eventType.includes('invalid')) {
    return 'bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-800';
  }
  if (eventType.includes('expired')) {
    return 'bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-300 dark:border-yellow-800';
  }
  if (eventType.includes('detected')) {
    return 'bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-800';
  }
  return 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800';
};

const formatTimestamp = (timestamp: string) => {
  const date = new Date(timestamp);
  return Number.isNaN(date.getTime()) ? timestamp : date.toLocaleString();
};

const SecurityAuditLogPage: React.FC = () => {
  const [logs, setLogs] = useState<SecurityAuditLog[]>([]);
  const [stats, setStats] = useState<SecurityAuditLogsStatsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalLogs, setTotalLogs] = useState(0);
  const [selected, setSelected] = useState<SecurityAuditLog | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [clearing, setClearing] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const logsAbortRef = useRef<AbortController | null>(null);

  const fetchLogs = useCallback(async () => {
    logsAbortRef.current?.abort();
    const controller = new AbortController();
    logsAbortRef.current = controller;

    setLoading(true);
    setError(null);

    try {
      const response = await adminService.getSecurityLogs({ limit: LOG_FETCH_ALL_LIMIT });
      if (controller.signal.aborted) return;
      setLogs(response.logs);
      setTotalLogs(response.total);
    } catch (err: any) {
      if (err.name === 'AbortError' || err.code === 'ERR_CANCELED') return;
      setError(err.response?.data?.detail || 'Failed to fetch security logs');
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      setStats(await adminService.getSecurityLogsStats());
    } catch (err: any) {
      console.error('Failed to fetch security log stats:', err);
    }
  }, []);

  const refresh = useCallback(async () => {
    await fetchLogs();
    await fetchStats();
  }, [fetchLogs, fetchStats]);

  const handleClearLogs = async () => {
    if (!window.confirm('Are you sure you want to clear all security audit logs? This action cannot be undone.')) {
      return;
    }

    setClearing(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const result = await adminService.clearSecurityLogs();
      setSuccessMessage(
        `Successfully cleared ${result.deleted_count.toLocaleString()} security events`
      );
      setSelected(null);
      await refresh();
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to clear security logs');
    } finally {
      setClearing(false);
    }
  };

  useEffect(() => {
    refresh();
    return () => logsAbortRef.current?.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useVisibilityAwareInterval(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, autoRefresh ? LOG_AUTO_REFRESH_MS : null);

  const columns = useMemo<ColumnDef<SecurityAuditLog>[]>(
    () => [
      {
        accessorKey: 'created_at',
        header: 'Timestamp',
        cell: (info) => (
          <span className="whitespace-nowrap">{formatTimestamp(info.getValue() as string)}</span>
        ),
        size: 175,
      },
      {
        accessorKey: 'event_type',
        header: 'Event Type',
        cell: (info) => {
          const v = info.getValue() as string;
          return (
            <span
              className={`px-2 py-0.5 rounded text-xs font-medium border ${getEventTypeBadgeColor(v)}`}
            >
              {v}
            </span>
          );
        },
        size: 220,
      },
      {
        accessorKey: 'severity',
        header: 'Severity',
        cell: (info) => <SeverityBadge severity={info.getValue() as string} />,
        size: 120,
      },
      {
        accessorKey: 'user_email',
        header: 'User',
        cell: (info) => {
          const v = info.getValue() as string | undefined;
          if (!v) return <span className="text-gray-400">—</span>;
          return (
            <span className="block truncate" title={v}>
              {v}
            </span>
          );
        },
        size: 220,
      },
      {
        accessorKey: 'ip_address',
        header: 'IP Address',
        cell: (info) => {
          const v = info.getValue() as string | undefined;
          return v || <span className="text-gray-400">—</span>;
        },
        size: 130,
      },
      {
        id: 'endpoint',
        accessorFn: (row) =>
          row.endpoint ? `${row.http_method || ''} ${row.endpoint}`.trim() : '',
        header: 'Endpoint',
        cell: (info) => {
          const v = info.getValue() as string;
          if (!v) return <span className="text-gray-400">—</span>;
          return (
            <span className="block truncate" title={v}>
              {v}
            </span>
          );
        },
        size: 380,
      },
    ],
    []
  );

  const truncated = totalLogs > logs.length;

  return (
    <div className="p-4 pb-24">
      {/* Header */}
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <ShieldExclamationIcon className="w-7 h-7 text-red-600" />
            <h1 className="text-xl font-bold text-heading">Security Audit Log</h1>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            IP binding violations, token failures and suspicious activity. Click any row for
            the full event details.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={refresh}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            title={`Auto-refresh every ${LOG_AUTO_REFRESH_MS / 1000}s`}
            className={`px-3 py-2 rounded-md border text-sm ${
              autoRefresh
                ? 'bg-green-50 border-green-300 text-green-700 dark:bg-green-900/30 dark:border-green-800 dark:text-green-300'
                : 'bg-gray-50 border-gray-300 text-label dark:bg-gray-700 dark:border-gray-600'
            }`}
          >
            Auto: {autoRefresh ? 'ON' : 'OFF'}
          </button>
          <button
            onClick={handleClearLogs}
            disabled={clearing || loading}
            className="flex items-center gap-2 px-3 py-2 bg-red-600 text-white rounded-md text-sm hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            <TrashIcon className="w-4 h-4" />
            {clearing ? 'Clearing…' : 'Clear Log'}
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          <div className="bg-panel rounded-lg shadow p-4 border border-gray-200 dark:border-gray-700">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total Events</div>
            <div className="text-2xl font-bold text-heading">
              {stats.total_events.toLocaleString()}
            </div>
          </div>
          <div className="bg-panel rounded-lg shadow p-4 border border-gray-200 dark:border-gray-700">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">High/Critical</div>
            <div className="text-2xl font-bold text-red-600">{stats.high_critical_count}</div>
          </div>
          <div className="bg-panel rounded-lg shadow p-4 border border-gray-200 dark:border-gray-700">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Events (24h)</div>
            <div className="text-2xl font-bold text-blue-600">{stats.recent_events_24h}</div>
          </div>
          <div className="bg-panel rounded-lg shadow p-4 border border-gray-200 dark:border-gray-700">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">
              High/Critical (24h)
            </div>
            <div className="text-2xl font-bold text-orange-600">
              {stats.recent_high_critical_24h}
            </div>
          </div>
        </div>
      )}

      {/* Success Message */}
      {successMessage && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md p-3 mb-4">
          <div className="flex items-center gap-2 text-green-800 dark:text-green-300">
            <InformationCircleIcon className="w-5 h-5" />
            <span className="font-medium">{successMessage}</span>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-3 mb-4">
          <div className="flex items-center gap-2 text-red-800 dark:text-red-300">
            <XCircleIcon className="w-5 h-5" />
            <span className="font-medium">{error}</span>
          </div>
        </div>
      )}

      {truncated && (
        <div className="mb-2 px-3 py-1.5 text-xs text-amber-800 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded">
          Showing the {logs.length.toLocaleString()} most recent of{' '}
          {totalLogs.toLocaleString()} events.
        </div>
      )}

      <TanTable
        data={logs}
        columns={columns}
        loading={loading}
        settingsKey="security-audit-log-table"
        fieldName="security-audit-log-table"
        cardTitle="Security Events"
        searchPlaceholder="Search by email, IP, endpoint…"
        enableSearch={true}
        enableColumnFilters={true}
        enableShowHideCols={true}
        enableReset={true}
        enableExcelExport={true}
        enableZebraStriping={true}
        showGridLines={true}
        columnFilterTypes={{
          created_at: 'text',
          user_email: 'text',
          ip_address: 'text',
          endpoint: 'text',
        }}
        defaultSorting={[{ id: 'created_at', desc: true }]}
        onRowClick={(row: SecurityAuditLog) => setSelected(row)}
      />

      <Drawer
        isOpen={!!selected}
        onClose={() => setSelected(null)}
        maxWidth="xl"
        width="56rem"
        title={
          selected ? (
            <div className="flex items-center gap-2">
              <SeverityBadge severity={selected.severity} />
              <span>{selected.event_type}</span>
              <span className="text-xs text-muted">
                {formatTimestamp(selected.created_at)}
              </span>
            </div>
          ) : (
            'Security Event'
          )
        }
      >
        {selected && (
          <div className="space-y-5 text-sm">
            <section>
              <div className="text-xs font-semibold text-muted uppercase tracking-wide mb-2">
                Event
              </div>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
                <DetailField label="Event Type">{selected.event_type}</DetailField>
                <DetailField label="Severity">{selected.severity}</DetailField>
                <DetailField label="Timestamp">
                  {formatTimestamp(selected.created_at)}
                </DetailField>
                <DetailField label="Event ID">{selected.id}</DetailField>
                <DetailField label="User">{selected.user_email || NONE}</DetailField>
                <DetailField label="IP Address">{selected.ip_address || NONE}</DetailField>
                <DetailField label="Endpoint">
                  {selected.endpoint ? (
                    <>
                      {selected.http_method} {selected.endpoint}
                    </>
                  ) : (
                    NONE
                  )}
                </DetailField>
                <DetailField label="User Agent">{selected.user_agent || NONE}</DetailField>
              </dl>
            </section>

            {selected.details && Object.keys(selected.details).length > 0 && (
              <section>
                <div className="text-xs font-semibold text-muted uppercase tracking-wide mb-2">
                  Event Details
                </div>
                <pre className="text-xs bg-gray-900 text-gray-100 p-3 rounded overflow-x-auto">
                  {JSON.stringify(selected.details, null, 2)}
                </pre>
              </section>
            )}
          </div>
        )}
      </Drawer>

      {logs.length === 0 && !loading && (
        <div className="text-center py-12">
          <InformationCircleIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600 dark:text-gray-400">No security events found</p>
        </div>
      )}
    </div>
  );
};

export default SecurityAuditLogPage;
